/**
 * Password-strength rules — frontend mirror of
 * backend/app/services/signup_validation_service.py.
 *
 * The backend is the source of truth: it'll reject weak passwords
 * with 422. We replicate the same rules here so the user gets
 * inline feedback as they type and the form's submit button can be
 * disabled until the password is valid. Keep the rule list in sync
 * with PASSWORD_MIN_LENGTH / PASSWORD_MAX_LENGTH / _BAD_PASSWORDS
 * on the backend.
 */

export const PASSWORD_MIN_LENGTH = 12
export const PASSWORD_MAX_LENGTH = 128


// Mirror of backend _BAD_PASSWORDS — case-insensitive comparison.
const BLOCKLIST: ReadonlySet<string> = new Set([
  'password', 'password123', 'password1234', 'passwordpassword',
  '12345678', '123456789', '1234567890',
  'qwerty', 'qwertyuiop',
  'letmein', 'welcome', 'welcome123',
  'admin', 'admin1234', 'administrator',
  'iloveyou', 'monkey',
  '1q2w3e4r5t', '1qaz2wsx',
  'aletlala', 'injez',
])


export type RuleId = 'length' | 'letter' | 'digit' | 'blocklist' | 'tooLong'


export interface Rule {
  id: RuleId
  /** True iff the password satisfies the rule. */
  passes: boolean
  /** Short message in the requested language. */
  message: string
}


export interface PasswordStrengthResult {
  /** Every rule, in display order. */
  rules: Rule[]
  /** True iff every rule passes. */
  valid: boolean
  /** 0–4 score for the visual meter. Always 0 if invalid. */
  score: number
  /** Categorical label for the meter. */
  label: 'empty' | 'weak' | 'fair' | 'good' | 'strong'
}


function ruleMessage(id: RuleId, lang: 'en' | 'ar'): string {
  const en: Record<RuleId, string> = {
    length: `At least ${PASSWORD_MIN_LENGTH} characters`,
    letter: 'At least one letter',
    digit: 'At least one digit',
    blocklist: 'Not a commonly-used password',
    tooLong: `At most ${PASSWORD_MAX_LENGTH} characters`,
  }
  const ar: Record<RuleId, string> = {
    length: `${PASSWORD_MIN_LENGTH} حرفاً على الأقل`,
    letter: 'حرف واحد على الأقل',
    digit: 'رقم واحد على الأقل',
    blocklist: 'ليست من كلمات المرور الشائعة',
    tooLong: `${PASSWORD_MAX_LENGTH} حرفاً كحد أقصى`,
  }
  return (lang === 'ar' ? ar : en)[id]
}


export function getPasswordStrength(
  password: string,
  lang: 'en' | 'ar' = 'en',
): PasswordStrengthResult {
  const rules: Rule[] = [
    {
      id: 'length',
      passes: password.length >= PASSWORD_MIN_LENGTH,
      message: ruleMessage('length', lang),
    },
    {
      id: 'letter',
      passes: /[A-Za-z]/.test(password),
      message: ruleMessage('letter', lang),
    },
    {
      id: 'digit',
      passes: /\d/.test(password),
      message: ruleMessage('digit', lang),
    },
    {
      id: 'blocklist',
      passes: password.length > 0 && !BLOCKLIST.has(password.toLowerCase()),
      message: ruleMessage('blocklist', lang),
    },
    {
      id: 'tooLong',
      passes: password.length === 0
        ? true
        : password.length <= PASSWORD_MAX_LENGTH,
      message: ruleMessage('tooLong', lang),
    },
  ]

  const valid = password.length > 0 && rules.every((r) => r.passes)

  // Score is # of (length, letter, digit, blocklist) rules satisfied
  // — 0..4. The "tooLong" rule isn't a positive signal so it doesn't
  // contribute. Empty input is "empty", not "weak".
  let score = 0
  if (password.length > 0) {
    score = rules
      .filter((r) => r.id !== 'tooLong')
      .reduce((acc, r) => acc + (r.passes ? 1 : 0), 0)
  }
  const label: PasswordStrengthResult['label'] =
    password.length === 0 ? 'empty'
    : score <= 1 ? 'weak'
    : score === 2 ? 'fair'
    : score === 3 ? 'good'
    : 'strong'

  return { rules, valid, score, label }
}
